/**
 * Queue a mission: it starts on the first machine that fits, now or when one frees up. Shows,
 * live, where it would start or why it would wait (the hub's own scheduler, run in the page).
 */
import { CircleCheck, CircleDashed, MonitorPlay, Plus, SquareTerminal } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ITEM_PRIORITY_LABEL, LABEL_RE, parseLabels } from '@orgus/protocol';
import type { Id, ItemPriority, Mission, SchedulerState, WaitReason } from '@orgus/protocol';
import { Button, Dialog, ErrorState, Field, Input, Segmented, Select, Textarea, toast, useNow } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { useMissionsState } from '../missions/data.ts';
import { enqueue } from './data.ts';
import { loadsFrom, previewPlacement } from './model.ts';

/**
 * Why it would wait, as one short line that names the limit holding it. The page's Room now
 * counts CPU and memory only; a mission can still wait on a queue, a rule or a limit.
 * The scheduler's full sentence is on hover.
 */
const WAIT_LINE: Record<WaitReason, string> = {
  'no-machine': 'Waits for a machine to connect',
  'no-checkout': 'Waits for a machine with this project',
  runtime: 'Waits for a machine with Claude Code',
  labels: 'Waits for a machine with these labels',
  'pinned-offline': 'Waits for its machine to come online',
  offline: 'Waits for a machine to come online',
  drained: 'Waits while the machines are drained',
  'time-rule': 'Waits for a time rule to allow work',
  headroom: 'Waits for CPU or memory to free up',
  'node-full': 'Waits for a free session slot',
  'work-full': 'Waits for the heavy work queue, not CPU or memory',
  'project-limit': 'Waits for the project limit',
  'person-limit': 'Waits for the limit per person',
  'project-busy': 'Waits while the project lands or deploys',
  paused: 'Waits until missions are resumed',
  restart: 'Waits for the hub to restart',
};

export function QueueDialog({ open, onOpenChange, state, missionId }: { open: boolean; onOpenChange: (v: boolean) => void; state: SchedulerState; missionId?: Id | null }) {
  const src = useOrgus(useShallow((s) => ({ machines: s.machines, sessions: s.sessions, checkouts: s.checkouts, ext: s.ext, projects: s.projects, mode: s.mode })));
  const now = useNow(5000);
  const missions = useMissionsState().state;
  const queued = useMemo(() => new Set(state.queue.filter((e) => e.state === 'queued' || e.state === 'starting').map((e) => e.missionId)), [state.queue]);
  const choices = useMemo(() => (missions?.missions ?? []).filter((m) => !m.closed && !queued.has(m.id)).sort((a, b) => b.updatedAt - a.updatedAt), [missions, queued]);

  const [id, setId] = useState<Id>('');
  const [labels, setLabels] = useState('');
  const [pin, setPin] = useState('');
  const [mode, setMode] = useState<'headless' | 'terminal'>('headless');
  const [priority, setPriority] = useState('2');
  const [note, setNote] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);

  const mission: Mission | undefined = choices.find((m) => m.id === id) ?? missions?.missions.find((m) => m.id === id);

  useEffect(() => {
    if (!open) return;
    const first = (missionId && choices.find((m) => m.id === missionId)) || choices[0];
    setId(first?.id ?? '');
    setLabels((first?.placement.labels ?? []).join(', '));
    setPin('');
    setMode('headless');
    setPriority('2');
    setNote('');
    setNoteOpen(false);
    setError(null);
    setBusy(false);
    // Only on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const badLabel = labels.split(/[\s,]+/).filter(Boolean).find((w) => !LABEL_RE.test(w.toLowerCase()));
  const needs = parseLabels(labels);
  const projectMachines = useMemo(() => {
    if (!mission) return [];
    const ids = new Set(Object.values(src.checkouts).filter((c) => c.projectId === mission.projectId).map((c) => c.machineId));
    return Object.values(src.machines).filter((m) => ids.has(m.id)).sort((a, b) => a.name.localeCompare(b.name));
  }, [mission, src.checkouts, src.machines]);

  // With the machines' load reports, as the Capacity page and the hub place it (ADR 0063 step 5):
  // without them the preview counted only session slots and could start where heavy work has no room.
  const loads = useMemo(() => loadsFrom(src.ext, now, src.mode === 'mock'), [src.ext, now, src.mode]);
  const preview = mission ? previewPlacement(state, { ...src, loads }, missions, now, { projectId: mission.projectId, labels: needs, machineId: pin || null, person: mission.owner, priority: Number(priority) }) : null;

  const submit = async () => {
    if (!mission || badLabel || busy) return;
    setBusy(true);
    setError(null);
    try {
      const e = await enqueue({ missionId: mission.id, needs: { labels: needs }, machineId: pin || null, mode, priority: Number(priority), ...(note.trim() ? { note: note.trim() } : {}) });
      const where = e.placedOn ? src.machines[e.placedOn]?.name : null;
      if (e.state === 'started' || e.state === 'starting') toast.success('Mission started', `The scheduler placed it on ${where ?? 'a machine'}.`);
      else if (e.state === 'failed') toast.error({ what: 'The machine did not start it.', why: e.message ?? 'It did not say why.', next: 'Fix the cause, then queue it again.' });
      else toast('Mission queued', { description: e.message ?? 'It starts when a machine that fits has room.' });
      onOpenChange(false);
    } catch (err) {
      setError(toUiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Queue mission"
      description="It starts on the first machine that fits, now or as soon as one frees up."
      size="lg"
      dismissible={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()} disabled={!mission || !!badLabel} shortcut={['mod', 'enter']}>
            {preview?.kind === 'start' ? 'Start now' : 'Add to queue'}
          </Button>
        </>
      }
    >
      {!choices.length && !mission ? (
        <p className="text-caption text-fg-muted">{missions?.missions.some((m) => !m.closed) ? 'Every open mission is already in the queue.' : 'There is no open mission to queue.'} Create one on Missions, or use Queue for any machine when you start one there.</p>
      ) : (
        <div
          className="flex flex-col gap-4"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void submit();
            }
          }}
        >
          <Select
            label="Mission"
            value={id}
            onValueChange={(v) => {
              setId(v);
              const m = choices.find((x) => x.id === v);
              setLabels((m?.placement.labels ?? []).join(', '));
              setPin('');
            }}
            options={choices.map((m) => ({ value: m.id, label: `${m.title} (${src.projects[m.projectId]?.name ?? 'unknown project'})` }))}
            placeholder="Choose a mission"
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {/* Empty reads as a quiet prompt, never as a value: mono only once labels are typed. */}
            <Input
              label="Needs labels"
              hint="Every one must be on the machine, for example linux, gpu."
              value={labels}
              onChange={(e) => setLabels(e.target.value)}
              error={badLabel ? `"${badLabel}" is not a label. Use letters, digits, dot, dash, colon or slash.` : undefined}
              placeholder="Add labels"
              className={labels ? 'font-mono' : undefined}
            />
            <Select
              label="Machine"
              value={pin}
              onValueChange={setPin}
              options={[{ value: '', label: 'Any machine that fits' }, ...projectMachines.map((m) => ({ value: m.id, label: `Only ${m.name}${m.online ? '' : ' (offline)'}` }))]}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Runs" className="min-w-0">
              {() => (
                <Segmented
                  label="Runs"
                  size="md"
                  value={mode}
                  onChange={setMode}
                  className="self-start"
                  options={[
                    { value: 'headless', label: 'Headless', icon: <MonitorPlay /> },
                    { value: 'terminal', label: 'In a terminal', icon: <SquareTerminal /> },
                  ]}
                />
              )}
            </Field>
            <Select
              label="Priority"
              value={priority}
              onValueChange={setPriority}
              options={([0, 1, 2, 3] as ItemPriority[]).map((p) => ({ value: String(p), label: ITEM_PRIORITY_LABEL[p] }))}
            />
          </div>
          {/* Optional, so folded: the note opens on request. */}
          {noteOpen ? (
            <Textarea label="Note for the session" rows={2} value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
          ) : (
            <Button variant="ghost" size="sm" icon={<Plus />} className="-ml-2 self-start" onClick={() => setNoteOpen(true)}>
              Add a note
            </Button>
          )}
          {/* Where it would go, as one quiet line: the same room the Capacity page counts. */}
          {preview && (
            <p role="status" className="flex items-center gap-2 text-caption text-fg-muted">
              {preview.kind === 'start' ? <CircleCheck className="size-4 shrink-0 text-fg-subtle" aria-hidden /> : <CircleDashed className="size-4 shrink-0 text-fg-subtle" aria-hidden />}
              <span className="min-w-0" title={preview.kind === 'wait' ? preview.message : undefined}>
                {preview.kind === 'start' ? `Starts now on ${preview.machineName}` : WAIT_LINE[preview.reason]}
              </span>
            </p>
          )}
          {error && <ErrorState variant="inline" error={error} />}
        </div>
      )}
    </Dialog>
  );
}
